// 05n — WHICH ENTRY DOES A STORED STRING NAME? One implementation, and the
// reason it is a file rather than a function is 05m step C's finding: the
// matcher existed TWICE, to different depths, and the shallower copy was a live
// defect (`ctc` matched three facets where `resolveTaxonomyLabel` matched five,
// so a case type chosen in French resolved for its NAME and not for its
// COLOUR). Step C merged those two. This slice needs the same question answered
// by a migration and by a census as well as by the browser, and a third copy —
// in a script that REWRITES 800 rows — is the same defect with the blast radius
// of a data migration.
//
// PURE ESM, no imports, no DOM, no prisma. Consumed by:
//   · src/lib/taxonomyHelpers.ts        the browser (resolveTaxonomyEntry)
//   · server/scripts/taxonomy_value_census.js     the read-only census
//   · server/scripts/seed_migration_23_*.js       the conversion
//   · recordings/05n-label-to-code/                the guard
// Node >= 20.19 requires ESM from CJS directly, which is how conflictsModule.js
// already reaches src/lib/coiMatch.mjs (server/src/lib/conflictsModule.js:45).

// ── the five facets ──────────────────────────────────────────────────────────
//
// A stored value may be the code or ANY of the four label columns, because
// `pickEntryLabel` returns labelFr to a French user and labelNl to a Dutch one
// and the select wrote back whatever it displayed. `label` is the legacy
// single-language column and is still populated on every row.
export const FACETS = ['code', 'labelEn', 'labelFr', 'labelNl', 'label'];

// ── the normalisation ladder ─────────────────────────────────────────────────
//
// EVERY RUNG BELOW `exact` IS A JUDGEMENT, and this module never acts on one.
// It reports which rung produced a match and what the match was; approving a
// fold is Laurent's, once, from a list (brief 23 §3). A normaliser wired into
// the running app would keep guessing forever — a table of approved literals
// stops after one run, which is why the migration takes literals and this
// ladder only ever writes a report.
//
// Cumulative and ordered least-lossy first, so the rung named in a proposal
// names everything that was applied to reach it. Ambiguity STOPS the ladder:
// a rung that yields two claimants cannot be resolved by loosening further,
// only merged with more.

// NFKD, then drop the combining marks (U+0300-U+036F), written as escapes
// because a literal range of invisible characters is unreadable in a diff and
// unreviewable in a PR. Same decomposition `slugifyCode` performs
// (server/src/routes/taxonomies.js).
const strip = (s) => s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');

export const LADDER = [
  { name: 'exact', fn: (s) => s },
  { name: 'trailing-stop-insensitive', fn: (s) => s.replace(/\.+$/, '') },
  { name: 'case-and-stop-insensitive', fn: (s) => s.replace(/\.+$/, '').toLowerCase() },
  {
    name: 'case-stop-and-space-insensitive',
    fn: (s) => s.replace(/\.+$/, '').toLowerCase().replace(/\s+/g, ' ').trim(),
  },
  {
    name: 'case-stop-space-and-accent-insensitive',
    fn: (s) => strip(s.replace(/\.+$/, '').toLowerCase().replace(/\s+/g, ' ').trim()),
  },
];

// ── exact matching, which is all the running app ever does ───────────────────

// Every entry that claims `value` on any facet. Returns ENTRIES, not labels,
// because callers want different things from one: the label, `meta.color`, or
// merely whether it resolved at all.
export function claimants(value, entries, normalise) {
  if (value == null || value === '') return [];
  if (!Array.isArray(entries) || entries.length === 0) return [];
  const n = normalise || ((s) => s);
  const want = n(String(value));
  if (want === '') return [];
  return entries.filter(
    (e) =>
      !!e &&
      FACETS.some((f) => {
        const v = e[f];
        return typeof v === 'string' && v !== '' && n(v) === want;
      }),
  );
}

// AMBIGUITY IS NOT A TIE TO BREAK. `person_title` "Mr." is `mr`'s labelEn and
// `me`'s labelNl, and `me` is Maitre/Meester — a different person entirely. Two
// claimants resolve to NOTHING, so the row keeps its stored string and says so,
// rather than a Dutch reader being shown "Dhr." for a row that may have meant
// Meester. THE FOUR RULES #4.
//
// Multiple FACETS of ONE entry are not ambiguity: `case_type/civil` carries
// labelEn and labelFr both "Civil", which is one answer arrived at twice. The
// filter is over entries, so that case yields exactly one claimant.
export function resolveEntry(value, entries) {
  const hits = claimants(value, entries);
  return hits.length === 1 ? hits[0] : null;
}

// What does this column value actually say?
//
//   ok          exactly one entry claims it
//   ambiguous   two or more DIFFERENT entries claim it — see above
//   unresolved  the vocabulary does not contain it (the renamed-entry orphan)
//   unknown     nothing to say: no value, or the kind has not loaded
//
// AN UNLOADED KIND IS NOT AN ORPHAN. `entries` is empty during hydration and
// after a failed fetch, and treating either as "no entry matches" would paint a
// re-pick warning across every row of a healthy database for one frame — the
// instrument crying wolf is how it stops being read. Absent evidence, `unknown`.
export function valueState(value, entries) {
  if (value == null || value === '') return 'unknown';
  if (!Array.isArray(entries) || entries.length === 0) return 'unknown';
  const hits = claimants(value, entries);
  return hits.length === 1 ? 'ok' : hits.length === 0 ? 'unresolved' : 'ambiguous';
}

// ── the proposal, which is a report and never an action ──────────────────────

/**
 * What could this unresolved value be? Walks the ladder and stops at the first
 * rung that says anything — a single claimant (a proposal) or several (an
 * ambiguity the looser rungs can only make worse).
 *
 * Returns one of:
 *   { state: 'ok',         entry }                    already exact; no fold needed
 *   { state: 'ambiguous',  rung, entries }            two or more claim it
 *   { state: 'proposed',   rung, entry }              ONE claimant, at a lossy rung
 *   { state: 'unmatched' }                            no rung matches anything
 *
 * `proposed` is the only one that is a candidate fold, and it is a candidate:
 * nothing in this repository turns a `proposed` into a write.
 */
export function propose(value, entries) {
  for (const rung of LADDER) {
    const hits = claimants(value, entries, rung.fn);
    if (hits.length === 1) {
      return rung.name === 'exact'
        ? { state: 'ok', entry: hits[0] }
        : { state: 'proposed', rung: rung.name, entry: hits[0] };
    }
    if (hits.length > 1) return { state: 'ambiguous', rung: rung.name, entries: hits };
  }
  return { state: 'unmatched' };
}
